export type KeyedCopyEntry = {
	key: string;
	text: string;
};

/** Shape used by CTA_COPY / COPY-style keyed copy constants. */
export type KeyedCopyConstant = {
	key: string;
	fallback: string;
};

/**
 * Returns trimmed text for the first keyed copy entry whose `key` matches.
 * Unknown keys or blank text yield an empty string. Pass `[]` when there is no list.
 */
export function getKeyedCopyText(
	entries: KeyedCopyEntry[],
	key: string
): string {
	const match = entries.find((entry) => entry.key === key);
	return match?.text?.trim() ?? "";
}

/**
 * Resolves CMS keyed copy for a `{key, fallback}` constant, falling back when
 * the entry is missing or blank.
 */
export function getKeyedCopy(
	entries: KeyedCopyEntry[],
	copy: KeyedCopyConstant
): string {
	return getKeyedCopyText(entries, copy.key) || copy.fallback;
}

export type KeyedCopyLinkParts = {
	before: string;
	label: string | null;
	after: string;
};

/**
 * Splits keyed copy that wraps a link label in `{{link}}…{{end-link}}`.
 * Tokens use the same `{{name}}` syntax as other keyed-copy placeholders.
 * Missing or empty markers yield `{before: text, label: null, after: ""}`.
 */
export function splitKeyedCopyLink(text: string): KeyedCopyLinkParts {
	const tokenPattern = /{{([^{}]+?)}}/g;

	let openStart = -1;
	let openEnd = -1;
	let closeStart = -1;
	let closeEnd = -1;
	let match: RegExpExecArray | null = tokenPattern.exec(text);

	while (match !== null) {
		const name = match[1]?.trim();
		if (name === "link" && openStart === -1) {
			openStart = match.index;
			openEnd = match.index + match[0].length;
		} else if (name === "end-link" && openStart !== -1) {
			closeStart = match.index;
			closeEnd = match.index + match[0].length;
			break;
		}
		match = tokenPattern.exec(text);
	}

	if (openStart === -1 || closeStart === -1) {
		return {before: text, label: null, after: ""};
	}

	const label = text.slice(openEnd, closeStart).trim();
	if (!label) {
		return {before: text, label: null, after: ""};
	}

	return {
		before: text.slice(0, openStart),
		label,
		after: text.slice(closeEnd),
	};
}
